// ---------------------------------------------------------------------------
// C76: Image Picker — drag-drop upload + library for page builder
// ---------------------------------------------------------------------------
import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, Image as ImageIcon, Trash2, X, Check } from "lucide-react";
import { useImageUpload, useImageLibrary, type UploadedImage } from "@/hooks/use-image-upload";
import { Skeleton } from "@/components/skeleton";

interface ImagePickerProps {
  onSelect: (image: UploadedImage) => void;
  onClose: () => void;
}

export default function ImagePicker({ onSelect, onClose }: ImagePickerProps) {
  const [tab, setTab] = useState<"upload" | "library">("upload");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading, progress, error } = useImageUpload();
  const { images, isLoading, error: libraryError, fetchImages, deleteImage } = useImageLibrary();

  useEffect(() => {
    if (tab === "library") fetchImages();
  }, [tab, fetchImages]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const result = await upload(file);
    if (result) onSelect(result);
  }, [upload, onSelect]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await upload(file);
    if (result) onSelect(result);
  }, [upload, onSelect]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[4px]" onClick={onClose} />
      <div className="relative z-10 w-[600px] max-w-[90vw] max-h-[80vh] rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Add Image</h3>
            <div className="flex rounded-lg border border-[var(--color-border)] p-0.5">
              <button
                onClick={() => setTab("upload")}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${tab === "upload" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-[var(--color-muted-foreground)]"}`}
              >
                Upload
              </button>
              <button
                onClick={() => setTab("library")}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${tab === "library" ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium" : "text-[var(--color-muted-foreground)]"}`}
              >
                Library
              </button>
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "upload" ? (
            <div>
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 cursor-pointer transition-colors ${
                  isDragging
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                    : "border-[var(--color-border)] hover:border-[var(--color-primary)]/50"
                }`}
              >
                {isUploading ? (
                  <>
                    <Skeleton className="mb-3 h-10 w-10 rounded-full" />
                    <Skeleton className="h-4 w-32" />
                    <div className="w-48 h-1.5 rounded-full bg-[var(--color-muted)] mt-2 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-[var(--color-muted-foreground)]/40 mb-3" />
                    <p className="text-sm font-medium text-[var(--color-foreground)]">
                      Drop an image here or click to browse
                    </p>
                    <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                      JPEG, PNG, WebP, GIF, SVG — Max 10MB
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              {error && (
                <p className="mt-3 text-xs text-[var(--color-destructive)] flex items-center gap-1">
                  <X className="h-3 w-3" /> {error}
                </p>
              )}
            </div>
          ) : (
            <div>
              {isLoading ? (
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]"
                    >
                      <Skeleton className="aspect-square w-full rounded-none" />
                      <div className="space-y-2 p-2">
                        <Skeleton className="h-3 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : libraryError ? (
                <div className="rounded-xl border border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5 px-4 py-5 text-sm text-[var(--color-destructive)]">
                  <p className="font-medium">Image library unavailable.</p>
                  <p className="mt-1">{libraryError}</p>
                </div>
              ) : images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/30" />
                  <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No uploaded images are available for this tenant yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img) => (
                    <div key={img.id} className="stagger-item group relative rounded-lg overflow-hidden border border-[var(--color-border)] aspect-square">
                      <img
                        src={img.thumbnailUrl}
                        alt={img.alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => onSelect(img)}
                          className="rounded-full bg-background p-2 shadow-md hover:bg-[var(--color-success)] hover:text-white transition-colors"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm("Delete this image permanently?")) {
                              await deleteImage(img.id);
                            }
                          }}
                          className="rounded-full bg-background p-2 shadow-md hover:bg-[var(--color-destructive)] hover:text-white transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent px-2 py-1">
                        <p className="text-[9px] text-white truncate">{img.format.toUpperCase()} · {formatBytes(img.size)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
